import { Fragment } from 'react';
import {
    Listbox,
    Transition,
    ListboxButton,
    ListboxOptions,
    ListboxOption,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import { MeetingAccess } from 'flow/utils/meeting-constants';

type MeetingAccessKey =
    | 'ANYONE_WITH_LINK'
    | 'ANYONE_IN_ORGANISATION'
    | 'MEMBERS_ONLY'
    | 'NO_ONE';

// Maps each backend enum key to the existing translation-key suffix (they
// don't all match 1:1, e.g. ANYONE_IN_ORGANISATION -> anyone_in_org).
const I18N_SUFFIX: Record<MeetingAccessKey, string> = {
    ANYONE_WITH_LINK: 'anyone_with_link',
    ANYONE_IN_ORGANISATION: 'anyone_in_org',
    MEMBERS_ONLY: 'members_only',
    NO_ONE: 'no_one',
};

type Props = {
    value: string | null | undefined;
    onChange: (value: MeetingAccessKey) => void;
    // "Members only" implies a persistent room's membership — meaningless
    // (and not offered) for a bare conference with no room behind it.
    hideMembersOnly?: boolean;
    // Dark variant, for the in-meeting host controls panel only — see
    // MessageGroupDivider/ActivateChatHistoryButton for the same pattern.
    videoDark?: boolean;
};

const MeetingAccessDropdown = ({
    value,
    onChange,
    hideMembersOnly = false,
    videoDark = false,
}: Props) => {
    const { i18next } = useAppContext();

    const allKeys = MeetingAccess.enums.map(
        (item) => item.key,
    ) as MeetingAccessKey[];
    const options = allKeys.filter(
        (key) => !hideMembersOnly || key !== 'MEMBERS_ONLY',
    );
    const optionLabel = (key: MeetingAccessKey) =>
        i18next.t(
            `meetings.create_room.meeting_access_option_${I18N_SUFFIX[key]}`,
        );
    const optionDescription = (key: MeetingAccessKey) =>
        i18next.t(
            `meetings.create_room.meeting_access_description_${I18N_SUFFIX[key]}`,
        );

    // Resolved against every key the client knows, not just the offered
    // `options` — a stored value that's filtered out here (e.g. MEMBERS_ONLY
    // under hideMembersOnly) must still show as selected and stay pickable
    // away from, or the control would disable itself and trap the host on it.
    // Falls back to nothing selected — not the most permissive option — when
    // the value hasn't loaded yet or doesn't match a key this client knows.
    const selectedKey = allKeys.includes(value as MeetingAccessKey)
        ? (value as MeetingAccessKey)
        : null;

    return (
        <div className="flex flex-col gap-y-3">
            <p
                className={twMerge(
                    'text-lg font-bold text-gray-800',
                    videoDark && 'text-white',
                )}
            >
                {i18next.t('meetings.create_room.meeting_access_label')}
            </p>

            <div className="flex flex-col gap-y-1">
                <p
                    className={twMerge(
                        'text-sm font-bold text-gray-800',
                        videoDark && 'text-white',
                    )}
                >
                    {i18next.t('meetings.create_room.meeting_access_question')}
                </p>

                <Listbox
                    value={selectedKey}
                    onChange={onChange}
                    disabled={!selectedKey}
                >
                    <div className="relative w-full text-sm/4">
                        <ListboxButton
                            className={twMerge(
                                'focus-ring w-full rounded-lg border-1 bg-gray-200 py-3 ps-3 pe-9 text-left text-gray-900 dark:bg-gray-100',
                                videoDark &&
                                    'border-gray-600 bg-gray-700 text-white dark:bg-gray-700',
                            )}
                        >
                            {selectedKey
                                ? optionLabel(selectedKey)
                                : i18next.t('videoconference.lobby.loading')}
                            <div className="absolute inset-y-0 end-0 flex items-center pe-2">
                                <Icon
                                    className={twMerge(
                                        'size-5 text-gray-600',
                                        videoDark && 'text-gray-300',
                                    )}
                                    iconKey="arrow_down"
                                    aria-hidden="true"
                                />
                            </div>
                        </ListboxButton>

                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <ListboxOptions
                                anchor={{ to: 'bottom start', gap: 4 }}
                                modal={false}
                                className={twMerge(
                                    'z-50 max-h-60 w-(--button-width) overflow-auto rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-hidden',
                                    videoDark &&
                                        'no-dark bg-gray-700 ring-gray-600',
                                )}
                            >
                                {options.map((key) => (
                                    <ListboxOption
                                        key={key}
                                        className={({ focus }) =>
                                            twMerge(
                                                'relative cursor-default py-2 ps-3 pe-9 select-none',
                                                focus
                                                    ? 'bg-gray-200'
                                                    : 'text-gray-900',
                                                videoDark &&
                                                    twMerge(
                                                        'text-white',
                                                        focus && 'bg-gray-600',
                                                    ),
                                            )
                                        }
                                        value={key}
                                    >
                                        {({ selected }) => (
                                            <span
                                                className={twMerge(
                                                    'truncate text-gray-500',
                                                    videoDark &&
                                                        'text-gray-300',
                                                    selected && 'font-medium',
                                                )}
                                            >
                                                {optionLabel(key)}
                                            </span>
                                        )}
                                    </ListboxOption>
                                ))}
                            </ListboxOptions>
                        </Transition>
                    </div>
                </Listbox>

                <p
                    className={twMerge(
                        'text-xs text-gray-500',
                        videoDark && 'text-gray-300',
                    )}
                >
                    {selectedKey ? optionDescription(selectedKey) : null}
                </p>
            </div>
        </div>
    );
};

export default MeetingAccessDropdown;
